import { useRef, useEffect } from 'react';

/**
 * 海面呼吸动画 — Canvas 2D
 * 
 * 模拟月光下的海面波动 + 光晕呼吸
 * 不追求精细水波，而是营造"整个海面在呼吸"的氛围
 */

export default function OceanBreathing({
  tintColor = 'rgba(8,18,40,0.6)',
  waveColor = 'rgba(100,180,230,0.04)',
  glowColor = 'rgba(255,235,170,0.03)',
  className,
  style,
}) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = rect.width + 'px';
      canvas.style.height = rect.height + 'px';
      ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener('resize', resize);

    const animate = (timestamp) => {
      const t = timestamp / 1000;
      const rect = canvas.parentElement.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;

      ctx.clearRect(0, 0, w, h);

      // 1. 海底光晕呼吸 — 暖色高光在远处海平线上
      const breathe = (Math.sin(t * 0.4) + 1) * 0.5; // 0-1 循环 15.7s
      const glowPulse = 0.5 + breathe * 0.5;

      const glowGrad = ctx.createRadialGradient(
        w * (0.5 + Math.sin(t * 0.07) * 0.1), // 光源缓慢漂移
        h * (0.15 + Math.sin(t * 0.12) * 0.05),
        0,
        w * 0.5, h * 0.15,
        h * 0.9
      );
      glowGrad.addColorStop(0, glowColor.replace(/[\d.]+\)$/, (glowPulse * 0.06) + ')'));
      glowGrad.addColorStop(0.4, 'rgba(100,180,230,0.01)');
      glowGrad.addColorStop(1, 'transparent');
      ctx.fillStyle = glowGrad;
      ctx.fillRect(0, 0, w, h);

      // 2. 海面波纹 — 多条平行波，从上到下
      ctx.save();
      for (let i = 0; i < 5; i++) {
        const yBase = h * (0.15 + i * 0.18);
        const waveHeight = 8 + i * 6;
        const speed = 0.6 + i * 0.3;
        const yOffset = Math.sin(t * speed + i) * 20;

        ctx.beginPath();
        ctx.moveTo(0, h);
        for (let x = 0; x <= w; x += 8) {
          const y = yBase + yOffset +
            Math.sin(x * 0.008 + t * speed + i * 0.7) * waveHeight +
            Math.sin(x * 0.015 - t * 0.5) * waveHeight * 0.3;
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.lineTo(w, h);
        ctx.closePath();

        const alpha = 0.03 + breathe * 0.02 + (i * 0.005);
        ctx.fillStyle = waveColor.replace(/[\d.]+\)$/, alpha + ')');
        ctx.fill();
      }
      ctx.restore();

      // 3. 月光反射区域 — 海面上的光斑
      for (let i = 0; i < 8; i++) {
        const seed = i * 137.5;
        const bx = ((seed * 2.3) % w);
        const by = h * (0.1 + ((seed * 1.1) % 0.25));
        const bSize = 30 + ((seed * 1.7) % 60);
        const bAlpha = 0.02 + (Math.sin(t * 0.5 + i) * 0.5 + 0.5) * 0.04;

        const bGrad = ctx.createRadialGradient(bx, by, 0, bx, by, bSize);
        bGrad.addColorStop(0, `rgba(255,240,200,${bAlpha})`);
        bGrad.addColorStop(1, 'transparent');
        ctx.fillStyle = bGrad;
        ctx.fillRect(bx - bSize, by - bSize, bSize * 2, bSize * 2);
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', resize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [tintColor, waveColor, glowColor]);

  return (
    <div
      className={className}
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        ...style,
      }}
    >
      <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: '100%' }} />
    </div>
  );
}
